// Root router del login portal.
//
// Todas las rutas viven bajo /:partnerId/ — el partner_id en el path permite
// branding por tenant sin necesidad de subdomain routing. El ThemeProvider
// se monta con el partnerId extraido del URL y fetchea el branding antes de
// renderizar el contenido.

import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ThemeProvider } from '@/theme/ThemeProvider';
import ThemeToggle from '@/components/ThemeToggle';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import Pay from '@/pages/Pay';
import Subscribe from '@/pages/Subscribe';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import ChangePassword from '@/pages/ChangePassword';
import Verify from '@/pages/Verify';
import DeviceVerify from '@/pages/DeviceVerify';
import NotFound from '@/pages/NotFound';

function PartnerShell(): JSX.Element {
  const { partnerId } = useParams<{ partnerId: string }>();

  // La ruta /:partnerId siempre captura un valor — si partnerId falta es un
  // bug de routing, no una situacion esperable en runtime.
  if (!partnerId) {
    return <NotFound />;
  }

  return (
    <ThemeProvider partnerId={partnerId}>
      <ThemeToggle />
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="signup" element={<Signup />} />
        <Route path="pay" element={<Pay />} />
        <Route path="subscribe" element={<Subscribe />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="change-password" element={<ChangePassword />} />
        <Route path="verify" element={<Verify />} />
        <Route path="device-verify" element={<DeviceVerify />} />
        <Route index element={<Navigate to="login" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ThemeProvider>
  );
}

// Partner default para acceso directo a `/`. En multi-tenant prod este
// fallback se desactiva (queda en NotFound + mensaje pidiendo URL valida);
// en dev y mientras tengamos un solo partner activo, redirigimos para que
// los equipos que prueban a mano encuentren algo util.
//
// VITE_DEFAULT_PARTNER_ID se setea en build-time. Si esta vacio, el root
// muestra NotFound como antes.
const DEFAULT_PARTNER = (import.meta.env.VITE_DEFAULT_PARTNER_ID as string | undefined) ?? '';

export default function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/:partnerId/*" element={<PartnerShell />} />
      <Route
        path="/"
        element={DEFAULT_PARTNER ? <Navigate to={`/${DEFAULT_PARTNER}/login`} replace /> : <NotFound />}
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
